import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarOff, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { WRITE_ROLES, useAuth } from "@/contexts/AuthContext";
import { addDays, addMinutes, api, fmtBR, fmtDate, formatApiError, mondayOf } from "@/lib/api";

const DAY_LABELS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const EMPTY_FORM = { teacher_id: "", course_id: "", modality_id: "", room_id: "", date: "", start_time: "", student_id: "none", notes: "", is_experimental: false };

export default function Schedule() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user.role);
  const isProfessor = user.role === "professor";
  const todayStr = fmtDate(new Date());
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [lessons, setLessons] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [lookups, setLookups] = useState({ teachers: [], courses: [], rooms: [], modalities: [], students: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [attLesson, setAttLesson] = useState(null);
  const [attForm, setAttForm] = useState({ attendance: "", diary_content: "", diary_notes: "" });
  const [attSaving, setAttSaving] = useState(false);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = async () => {
    const params = { start: fmtDate(days[0]), end: fmtDate(days[6]) };
    const [l, t, c, r, m, h, s] = await Promise.all([
      api.get("/lessons", { params }),
      api.get("/teachers"),
      api.get("/courses"),
      api.get("/rooms"),
      api.get("/modalities"),
      api.get("/holidays", { params: { year: days[0].getFullYear() } }),
      canWrite ? api.get("/students") : Promise.resolve({ data: [] }),
    ]);
    setLessons(l.data);
    setLookups({
      teachers: t.data.filter((x) => x.active),
      courses: c.data.filter((x) => x.active),
      rooms: r.data.filter((x) => x.active),
      modalities: m.data.filter((x) => x.active),
      students: s.data.filter((x) => x.status === "ativo"),
    });
    setHolidays(h.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const name = (list, id) => lookups[list].find((x) => x.id === id)?.name || "—";
  const holidayOf = (dateStr) => holidays.find((h) => h.date === dateStr);

  const teacherCourses = useMemo(() => {
    const teacher = lookups.teachers.find((t) => t.id === form.teacher_id);
    if (!teacher) return [];
    return lookups.courses.filter((c) => teacher.course_ids.includes(c.id));
  }, [form.teacher_id, lookups]);

  const selectedModality = lookups.modalities.find((m) => m.id === form.modality_id);
  const computedEnd = form.start_time && selectedModality ? addMinutes(form.start_time, form.is_experimental ? 20 : selectedModality.duration_minutes) : null;

  const openDialog = (lesson, presetDate) => {
    setConflicts([]);
    if (lesson) {
      setEditing(lesson);
      setForm({
        teacher_id: lesson.teacher_id, course_id: lesson.course_id, modality_id: lesson.modality_id,
        room_id: lesson.room_id, date: lesson.date, start_time: lesson.start_time,
        student_id: lesson.student_id || "none", notes: lesson.notes || "",
        is_experimental: lesson.is_experimental || false,
      });
    } else {
      setEditing(null);
      setForm({ ...EMPTY_FORM, date: presetDate || fmtDate(new Date()) });
    }
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setConflicts([]);
    try {
      const payload = { ...form };
      if (!payload.student_id || payload.student_id === "none") delete payload.student_id;
      if (editing) {
        await api.put(`/lessons/${editing.id}`, payload);
        toast.success("Aula atualizada.");
      } else {
        await api.post("/lessons", payload);
        toast.success("Aula agendada.");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && Array.isArray(detail)) setConflicts(detail);
      else setConflicts([formatApiError(err)]);
    } finally {
      setSaving(false);
    }
  };

  const cancelLesson = async () => {
    try {
      await api.put(`/lessons/${editing.id}`, { status: "cancelada" });
      toast.success("Aula cancelada.");
      setDialogOpen(false);
      load();
    } catch (err) {
      setConflicts([formatApiError(err)]);
    }
  };

  const openAttendance = (lesson) => {
    setAttLesson(lesson);
    setAttForm({
      attendance: lesson.attendance || "",
      diary_content: lesson.diary_content || "",
      diary_notes: lesson.diary_notes || "",
    });
  };

  const saveAttendance = async (e) => {
    e.preventDefault();
    setAttSaving(true);
    try {
      await api.put(`/lessons/${attLesson.id}/attendance`, {
        attendance: attForm.attendance,
        diary_content: attForm.diary_content,
        diary_notes: attForm.diary_notes,
      });
      toast.success(attLesson.attendance ? "Registro corrigido." : "Aula registrada.");
      setAttLesson(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAttSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Agenda</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Semana de {fmtBR(fmtDate(days[0]))} a {fmtBR(fmtDate(days[6]))}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="schedule-prev-week">
            <ChevronLeft size={16} />
          </Button>
          <Button variant="outline" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="schedule-today-button">
            Hoje
          </Button>
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="schedule-next-week">
            <ChevronRight size={16} />
          </Button>
          {canWrite && (
            <Button onClick={() => openDialog(null)} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="lesson-add-button">
              <Plus size={16} className="mr-2" /> Nova aula
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        {days.map((day) => {
          const dateStr = fmtDate(day);
          const holiday = holidayOf(dateStr);
          const dayLessons = lessons
            .filter((l) => l.date === dateStr)
            .sort((a, b) => a.start_time.localeCompare(b.start_time));
          return (
            <div key={dateStr} className="min-h-40 border border-border bg-white" data-testid={`schedule-day-${dateStr}`}>
              <div className="border-b border-border bg-zinc-50 px-3 py-2">
                <p className="font-heading text-sm font-semibold">
                  {DAY_LABELS[(day.getDay() + 6) % 7]} {fmtBR(dateStr).slice(0, 5)}
                </p>
                {holiday && (
                  <div className={`mt-1 flex items-center gap-1 text-[11px] ${holiday.has_exception ? "text-primary" : "text-destructive"}`} data-testid={`holiday-badge-${dateStr}`}>
                    <CalendarOff size={12} />
                    {holiday.name}{holiday.has_exception ? " (exceção: há aulas)" : ""}
                  </div>
                )}
              </div>
              <div className="space-y-2 p-2">
                {dayLessons.length === 0 && <p className="p-1 text-xs text-muted-foreground">—</p>}
                {dayLessons.map((l) => {
                  const canAttend = isProfessor && l.status !== "cancelada" && l.date <= todayStr;
                  const clickable = canWrite || canAttend;
                  return (
                  <button
                    key={l.id}
                    type="button"
                    disabled={!clickable}
                    onClick={() => (canWrite ? openDialog(l) : openAttendance(l))}
                    className={`w-full border border-border border-l-4 p-2 text-left transition-colors ${
                      l.status === "cancelada" ? "border-l-zinc-300 opacity-50" : "border-l-primary hover:bg-zinc-50"
                    }`}
                    data-testid={`lesson-card-${l.id}`}
                  >
                    <p className={`font-heading text-sm font-bold ${l.status === "cancelada" ? "line-through" : ""}`}>
                      {l.start_time}–{l.end_time}
                    </p>
                    <p className="text-xs font-medium">{name("teachers", l.teacher_id)}</p>
                    <p className="text-xs text-muted-foreground">{name("courses", l.course_id)} · {name("rooms", l.room_id)}</p>
                    {l.student_name && <p className="mt-1 text-xs">Aluno: {l.student_name}</p>}
                    {l.status === "cancelada" && <Badge variant="secondary" className="mt-1 text-[10px]">Cancelada</Badge>}
                    {l.attendance === "presente" && (
                      <Badge className="mt-1 bg-primary text-[10px] text-primary-foreground" data-testid={`lesson-attendance-${l.id}`}>Presente</Badge>
                    )}
                    {l.attendance === "falta" && (
                      <Badge variant="destructive" className="mt-1 text-[10px]" data-testid={`lesson-attendance-${l.id}`}>Falta</Badge>
                    )}
                    {l.is_experimental && (
                      <Badge variant="secondary" className="mt-1 text-[10px]" data-testid={`lesson-experimental-${l.id}`}>Experimental</Badge>
                    )}
                    {canAttend && !l.attendance && (
                      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-primary" data-testid={`lesson-register-cta-${l.id}`}>
                        Registrar aula
                      </p>
                    )}
                  </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="lesson-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar aula" : "Nova aula"}</DialogTitle>
            <DialogDescription>
              {editing ? "Altere os dados da aula ou cancele." : "Preencha os dados para agendar uma aula avulsa."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {conflicts.length > 0 && (
              <div className="border border-destructive/50 bg-destructive/10 p-3" data-testid="lesson-conflicts-alert">
                <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
                  <AlertTriangle size={16} /> Não foi possível salvar
                </p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-destructive">
                  {conflicts.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="space-y-2">
              <Label>Professor</Label>
              <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v, course_id: "" })}>
                <SelectTrigger data-testid="lesson-teacher-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {lookups.teachers.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Curso</Label>
                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })} disabled={!form.teacher_id}>
                  <SelectTrigger data-testid="lesson-course-select"><SelectValue placeholder={form.teacher_id ? "Selecione" : "Escolha o professor"} /></SelectTrigger>
                  <SelectContent>
                    {teacherCourses.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Modalidade</Label>
                <Select value={form.modality_id} onValueChange={(v) => setForm({ ...form, modality_id: v })}>
                  <SelectTrigger data-testid="lesson-modality-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {lookups.modalities.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name} — {m.duration_minutes} min (R$ {m.monthly_price.toFixed(2)}/mês)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={form.is_experimental}
                onChange={(e) => setForm({ ...form, is_experimental: e.target.checked })}
                data-testid="lesson-experimental-checkbox"
              />
              Aula experimental (duração fixa de 20 minutos)
            </label>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>Data</Label>
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="lesson-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Início</Label>
                <Input type="time" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="lesson-start-input" />
              </div>
              <div className="space-y-2">
                <Label>Sala</Label>
                <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
                  <SelectTrigger data-testid="lesson-room-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {lookups.rooms.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {computedEnd && (
              <p className="border border-border bg-zinc-50 p-2 text-sm" data-testid="lesson-computed-end">
                Término calculado: <strong>{computedEnd}</strong> ({form.is_experimental ? 20 : selectedModality.duration_minutes} min)
              </p>
            )}
            <div className="space-y-2">
              <Label>Aluno (opcional)</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="lesson-student-select"><SelectValue placeholder="Sem aluno vinculado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem aluno vinculado</SelectItem>
                  {(lookups.students || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lesson-notes-input" />
            </div>
            <div className="flex flex-col gap-2">
              <Button type="submit" disabled={saving || !form.teacher_id || !form.course_id || !form.modality_id || !form.room_id} className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="lesson-save-button">
                {saving ? "Salvando..." : editing ? "Salvar alterações" : "Agendar aula"}
              </Button>
              {editing && editing.status !== "cancelada" && editing.date <= todayStr && (
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); openAttendance(editing); }} className="w-full" data-testid="lesson-attendance-button">
                  Registrar frequência / diário
                </Button>
              )}
              {editing && editing.status !== "cancelada" && (
                <Button type="button" variant="outline" onClick={cancelLesson} className="w-full border-destructive/50 text-destructive transition-colors hover:bg-destructive/10" data-testid="lesson-cancel-button">
                  Cancelar aula
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!attLesson} onOpenChange={() => setAttLesson(null)}>
        <DialogContent data-testid="attendance-dialog">
          <DialogHeader>
            <DialogTitle>
              Registrar aula — {attLesson && `${fmtBR(attLesson.date)} · ${attLesson.start_time}–${attLesson.end_time}`}
            </DialogTitle>
            <DialogDescription>
              {attLesson?.student_name ? `Aluno: ${attLesson.student_name}` : "Registre a frequência e o diário da aula."}
              {attLesson?.attendance ? " Esta aula já possui registro — salvar irá corrigi-lo (com auditoria)." : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveAttendance} className="space-y-4">
            <div className="space-y-2">
              <Label>Frequência *</Label>
              <Select value={attForm.attendance} onValueChange={(v) => setAttForm({ ...attForm, attendance: v })}>
                <SelectTrigger data-testid="attendance-status-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="presente">Presente</SelectItem>
                  <SelectItem value="falta">Falta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Conteúdo trabalhado</Label>
              <Textarea rows={2} value={attForm.diary_content} onChange={(e) => setAttForm({ ...attForm, diary_content: e.target.value })} data-testid="attendance-content-input" />
            </div>
            <div className="space-y-2">
              <Label>Observação pedagógica</Label>
              <Textarea rows={2} value={attForm.diary_notes} onChange={(e) => setAttForm({ ...attForm, diary_notes: e.target.value })} data-testid="attendance-notes-input" />
            </div>
            <Button
              type="submit"
              disabled={attSaving || !attForm.attendance}
              className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
              data-testid="attendance-save-button"
            >
              {attSaving ? "Salvando..." : attLesson?.attendance ? "Salvar correção" : "Salvar registro"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
